/**
 * Sentry Error Monitoring Setup
 *
 * To enable Sentry error monitoring:
 * 1. Sign up at https://sentry.io and create a new project for React
 * 2. Copy your DSN from the project settings
 * 3. Set REACT_APP_SENTRY_DSN in your .env file
 * 4. Rebuild — initSentry() is called automatically from src/index.js
 *
 * @sentry/react is already listed as a dependency in package.json.
 */

import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry error monitoring.
 * Call this at the top of your app (e.g., in src/index.js before ReactDOM.render).
 * Safe to call even if REACT_APP_SENTRY_DSN is not set — it will simply skip init.
 */
export function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // No DSN configured — leave Sentry disabled.
    return;
  }

  Sentry.init({
    dsn,
    integrations: [
      // Automatically capture browser errors and performance data
      Sentry.browserTracingIntegration(),
    ],
    // Set traces sample rate to 1.0 to capture 100% of transactions for monitoring
    // Adjust this in production to reduce volume
    tracesSampleRate: 1.0,
    // Set environment
    environment: process.env.NODE_ENV,
    // Release version (optional — can be set from your CI/CD)
    // release: process.env.REACT_APP_VERSION,

    // Filter out noisy errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
  });
}

/**
 * Manually capture an error to Sentry.
 * Useful for catching errors in try/catch blocks that shouldn't crash the app.
 */
export function captureError(error, context = {}) {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.captureException(error, { extra: context });
  } else {
    console.error("[Sentry] Error captured (Sentry not initialized):", error, context);
  }
}

/**
 * Set the current user context for Sentry.
 * This allows you to see which users are affected by errors.
 */
export function setUserContext(user) {
  if (!process.env.REACT_APP_SENTRY_DSN) return;
  if (user) {
    Sentry.setUser({
      id: user.id,
      username: user.username,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
}
